import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  AcademicCapIcon,
  CalendarIcon,
  ArrowRightIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { AppDispatch } from '../../store'
import { register, login } from '../../store/slices/authSlice'
import { driveAPI } from '../../services/api'
import { uploadResume, registerWithResume } from '../../services/resumeAPI'
import CollegeSelect from '../../components/CollegeSelect'
import { College } from '../../services/collegeAPI'
import { Drive } from '../../types'

interface RegistrationForm {
  firstName: string
  lastName: string
  email: string
  password: string
  contactNumber: string
  aadharNumber: string
  college: string
  course: string
  branch: string
  collegePassoutYear: string
  collegePassoutMonth: string
  rollNumber: string
  resume: File | null
  tenthPercentage: string
  twelfthPercentage: string
  collegePercentage: string
}

const DriveRegistrationPage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [driveInfo, setDriveInfo] = useState<Drive | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRegistering, setIsRegistering] = useState(false)
  const [selectedCollege, setSelectedCollege] = useState<College | null>(null)
  
  // Track last shown toast to prevent duplicate toasts
  const lastToastStatus = useRef<string | null>(null)
  const resumeAccountEmail = useRef<string | null>(null)
  const [formData, setFormData] = useState<RegistrationForm>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    contactNumber: '',
    aadharNumber: '',
    college: '',
    course: '',
    branch: '',
    collegePassoutYear: '',
    collegePassoutMonth: '',
    rollNumber: '',
    resume: null,
    tenthPercentage: '',
    twelfthPercentage: '',
    collegePercentage: ''
  })

  useEffect(() => {
    loadDriveInfo()
  }, [linkId])

  // Auto-refresh drive info every 5 seconds to catch status changes
  useEffect(() => {
    const interval = setInterval(() => {
      //console.log('Auto-refreshing drive info...')
      loadDriveInfo()
    }, 5000) // 5 seconds

    return () => clearInterval(interval)
  }, [linkId])

  const loadDriveInfo = async () => {
    if (!linkId) {
      setIsLoading(false)
      toast.error('Invalid registration link')
      return
    }

    try {
      //console.log('Loading drive info for linkId:', linkId)
      
      // Get drive from API with timestamp to prevent caching
      const timestamp = Date.now()
      const randomId = Math.random().toString(36).substring(7)
      const response = await driveAPI.getDriveByLink(linkId + `?t=${timestamp}&r=${randomId}`)
      const drive = response.data
      
      // console.log('Found drive:', drive)
      // console.log('Drive start_date:', drive.start_date)
      // console.log('Drive end_date:', drive.end_date)
      // console.log('Drive status:', drive.status)
      // console.log('Current time:', new Date().toISOString())
      
      // Check if drive is active - only allow registration for active drives
      if (drive.status !== 'active') {
        console.log('Drive is not active, status:', drive.status)
        // Set drive info but mark it as not available for registration
        setDriveInfo({ ...drive, registrationAvailable: false })
        
        // Only show toast if status has changed
        if (lastToastStatus.current !== drive.status) {
          if (drive.status === 'upcoming') {
            toast.error('Drive has not started yet')
          } else if (drive.status === 'completed') {
            toast.error('Drive registration has ended')
          } else {
            toast.error('Drive is not currently accepting registrations')
          }
          lastToastStatus.current = drive.status
        }
        setIsLoading(false)
        return
      }
      
      // Reset toast status when drive becomes active
      lastToastStatus.current = null
      
      setDriveInfo({ ...drive, registrationAvailable: true })
      setIsLoading(false)
      
    } catch (error: any) {
      const statusCode = error.response?.status
      const is502Error = statusCode === 502
      
      // Handle 502 Bad Gateway errors gracefully (transient server issues)
      if (is502Error) {
        // Log a simple message instead of the full error object to reduce console noise
        console.warn('502 Bad Gateway - server temporarily unavailable, will retry automatically')
        // Don't show error toast for 502 errors (they're transient)
        // Don't clear driveInfo if we already have it (preserve last known good state)
        // Only set isLoading to false if this is the first load attempt
        if (!driveInfo) {
          setIsLoading(false)
        }
        return
      }
      
      // For other errors, show user-facing error only if we don't have drive info
      console.error('Error loading drive info:', error)
      if (!driveInfo) {
        const errorMessage = error.response?.data?.detail || 'Failed to load drive information'
        toast.error(errorMessage)
        setDriveInfo(null)
      }
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setFormData(prev => ({
      ...prev,
      resume: file
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsRegistering(true)

    try {
      // Validate form
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.contactNumber || 
          !selectedCollege || !formData.college || !formData.course || !formData.branch || !formData.collegePassoutYear || !formData.collegePassoutMonth || !formData.rollNumber ||
          !formData.tenthPercentage || !formData.twelfthPercentage || !formData.collegePercentage) {
        toast.error('Please fill in all required fields')
        setIsRegistering(false)
        return
      }

      // Validate password (length and bcrypt 72-byte limit)
      if (formData.password.length < 8) {
        toast.error('Password must be at least 8 characters long')
        setIsRegistering(false)
        return
      }
      // Enforce bcrypt 72-byte limit (UTF-8 byte length)
      try {
        const byteLen = new TextEncoder().encode(formData.password).length
        if (byteLen > 72) {
          toast.error('Password is too long (bcrypt limit 72 bytes). Please shorten it.')
          setIsRegistering(false)
          return
        }
      } catch {}

      // Validate resume file
      if (!formData.resume) {
        toast.error('Please upload your resume')
        setIsRegistering(false)
        return
      }

      // Validate file type and size
      const allowedTypes = ['application/pdf', 'application/x-pdf', 'application/octet-stream', '']
      if (!/\.pdf$/i.test(formData.resume.name) || !allowedTypes.includes(formData.resume.type)) {
        toast.error('Please upload a PDF file only')
        setIsRegistering(false)
        return
      }

      if (formData.resume.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Resume file size should be less than 5MB')
        setIsRegistering(false)
        return
      }

      // Check if drive is still accepting registrations
      if (!driveInfo) {
        toast.error('Drive information not found')
        setIsRegistering(false)
        return
      }

      // Validate Aadhar Number (must be exactly 12 digits)
      if (!formData.aadharNumber || formData.aadharNumber.length !== 12 || !/^\d{12}$/.test(formData.aadharNumber)) {
        toast.error('Aadhar Number must be exactly 12 digits')
        setIsRegistering(false)
        return
      }

      // Validate percentages are numbers between 0 and 100
      const tenth = parseFloat(formData.tenthPercentage)
      const twelfth = parseFloat(formData.twelfthPercentage)
      const collegePercent = parseFloat(formData.collegePercentage)
      if (Number.isNaN(tenth) || tenth < 0 || tenth > 100) {
        toast.error('10th Percentage must be a number between 0 and 100')
        setIsRegistering(false)
        return
      }
      if (Number.isNaN(twelfth) || twelfth < 0 || twelfth > 100) {
        toast.error('12th Percentage must be a number between 0 and 100')
        setIsRegistering(false)
        return
      }
      if (Number.isNaN(collegePercent) || collegePercent < 0 || collegePercent > 100) {
        toast.error('College/University Percentage must be a number between 0 and 100')
        setIsRegistering(false)
        return
      }

      if (resumeAccountEmail.current !== formData.email) {
        // Step 1: Create user account
        //console.log('Creating user account...')
        const registrationData = {
          email: formData.email,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone_number: formData.contactNumber,
          role: 'candidate'
        }

        const registerResult = await dispatch(register(registrationData))

        if (registerResult.type !== 'auth/register/fulfilled') {
          const errMsg = (registerResult as any)?.payload?.detail || (registerResult as any)?.payload || 'Registration failed. Please try again.'
          toast.error(errMsg)
          setIsRegistering(false)
          return
        }
        //console.log('User account created successfully')

        // Step 2: Auto-login the user
       // console.log('Auto-logging in user...')
        const loginResult = await dispatch(login({
          email: formData.email,
          password: formData.password
        }))

        if (loginResult.type !== 'auth/login/fulfilled') {
          const errMsg = (loginResult as any)?.payload || 'Login failed. Please try again.'
          toast.error(errMsg)
          setIsRegistering(false)
          return
        }

        //console.log('User logged in successfully')

        resumeAccountEmail.current = formData.email
      }

      // Upload directly to private S3 using a short-lived, constrained grant.
      const resumeUpload = await uploadResume(formData.resume!, driveInfo.registration_link)

      // Step 4: Register for the drive via API (required)
      const driveRegistrationData = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        contactNumber: formData.contactNumber,
        aadharNumber: formData.aadharNumber,
        college: formData.college,
        college_id: selectedCollege!.college_id,
        course: formData.course,
        branch: formData.branch,
        collegePassoutYear: formData.collegePassoutYear,
        collegePassoutMonth: formData.collegePassoutMonth,
        rollNumber: formData.rollNumber,
        tenthPercentage: formData.tenthPercentage,
        twelfthPercentage: formData.twelfthPercentage,
        collegePercentage: formData.collegePercentage,
        resume: resumeUpload
      }

      try {
        const resp = await registerWithResume({
          registration_link: driveInfo.registration_link,
          registration_data: driveRegistrationData
        })
        //console.log('Drive registration completed successfully', resp.data)
      } catch (e: any) {
        console.error('Drive registration failed', e?.response?.data || e?.message)
        const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Drive registration failed'
        toast.error(detail)
        setIsRegistering(false)
        return
      }

      // Step 5: Redirect to dashboard after successful drive registration
      toast.success('Registration successful! You are now logged in.')
      navigate('/dashboard')

    } catch (error) {
      console.error('Error registering candidate:', error)
      toast.error('Failed to register. Please try again.')
    } finally {
      setIsRegistering(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading drive information...</p>
        </div>
      </div>
    )
  }

  if (!driveInfo || !(driveInfo as any).registrationAvailable) {
    const isUpcomingDrive = driveInfo && driveInfo.status === 'upcoming'
    const isCompletedDrive = driveInfo && driveInfo.status === 'completed'
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto">
          <div className={`border rounded-lg p-4 mb-4 ${
            isUpcomingDrive 
              ? 'bg-yellow-50 border-yellow-200' 
              : isCompletedDrive
              ? 'bg-red-50 border-red-200'
              : 'bg-blue-50 border-blue-200'
          }`}>
            <h3 className={`font-semibold mb-2 ${
              isUpcomingDrive ? 'text-yellow-800' : isCompletedDrive ? 'text-red-800' : 'text-blue-800'
            }`}>
              Drive Status
            </h3>
            <p className={`text-sm ${
              isUpcomingDrive ? 'text-yellow-700' : isCompletedDrive ? 'text-red-700' : 'text-blue-700'
            }`}>
              {isUpcomingDrive 
                ? 'This Drive is not active for registration.'
                : isCompletedDrive
                ? 'Drive registration has ended.'
                : 'This Drive is not active for registration.'
              }
            </p>
            
            {/* Only show detailed instructions for upcoming drives */}
            {isUpcomingDrive && (
              <div className="mt-4 text-sm text-yellow-700">
                <div className="space-y-3">
                  {driveInfo.start_date && (
                    <div>
                      <div className="font-semibold">1. Registration Link will gets Active From:</div>
                      <div className="ml-12">{new Date(driveInfo.start_date).toLocaleString()}</div>
                    </div>
                  )}
                  <div>
                    <div className="font-semibold">2. Assessment will starts immediate after registration</div>
                    <div className="ml-12">Just wait few seconds after register and login.</div>
                  </div>
                  <div>
                    <div className="font-semibold">3. You will be moved to Your Dashboard</div>
                    <div className="ml-12">to Access the Assessment.</div>
                  </div>
                  <div>
                    <div className="font-semibold">4. Start Your Test Just after Register and Login.</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="btn btn-outline w-full"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Drive Registration
          </h1>
          <p className="text-gray-600">Register for the recruitment drive</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Drive Information - Left Side */}
          <div className="lg:w-96 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <CalendarIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Drive Details
                </h2>
                
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{driveInfo.title}</h3>
                    <p className="text-gray-600 text-sm mt-1">{driveInfo.description}</p>
                  </div>

                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className="capitalize">{driveInfo.college_type}</span>
                    {driveInfo.role && (
                      <>
                        <span>•</span>
                        <span className="font-medium">{driveInfo.role}</span>
                      </>
                    )}
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="w-4 h-4 mr-2" />
                    <span>Registration ends: {new Date(driveInfo.end_date).toLocaleString()}</span>
                  </div>

                </div>
              </div>

              {/* Assessment Summary */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <ClockIcon className="w-5 h-5 mr-2 text-green-600" />
                  Test Structure
                </h3>
                <div className="space-y-2">
                  {Object.entries(driveInfo.assessment_configs).map(([type, config]: [string, any]) => (
                    <div key={type} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 capitalize">{type.replace(/-/g, ' ')}</span>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">{config.duration_minutes} min</span>
                        <span className="text-gray-500 mx-1">•</span>
                        <span className="text-gray-600">{config.num_questions} questions</span>
                      </div>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-2 flex items-center justify-between font-semibold">
                    <span className="text-gray-900">Total Duration</span>
                    <span className="text-blue-600">{driveInfo.total_duration_minutes} minutes</span>
                  </div>
                </div>
              </div>

              {driveInfo.instructions && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Instructions</h3>
                  <p className="text-sm text-gray-600">{driveInfo.instructions}</p>
                </div>
              )}
            </div>
          </div>

          {/* Registration Form - Right Side */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <UserIcon className="w-5 h-5 mr-2 text-green-600" />
                Student Registration
              </h2>

            <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Minimum 8 characters"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This will be your login password for accessing tests
                </p>
              </div>

              <div>
                <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Number *
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  required
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="aadharNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Aadhar Number *
                </label>
                <input
                  type="text"
                  id="aadharNumber"
                  name="aadharNumber"
                  required
                  value={formData.aadharNumber}
                  onChange={handleInputChange}
                  maxLength={12}
                  pattern="[0-9]{12}"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter 12-digit Aadhar number"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter your 12-digit Aadhar number
                </p>
              </div>

              {/* 10th Percentage */}
              <div>
                <label htmlFor="tenthPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                  10th Percentage *
                </label>
                <input
                  type="number"
                  id="tenthPercentage"
                  name="tenthPercentage"
                  required
                  min={0}
                  max={100}
                  step="0.01"
                  value={formData.tenthPercentage}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none'
                  } as React.CSSProperties}
                />
              </div>

              {/* 12th Percentage */}
              <div>
                <label htmlFor="twelfthPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                  12th Percentage *
                </label>
                <input
                  type="number"
                  id="twelfthPercentage"
                  name="twelfthPercentage"
                  required
                  min={0}
                  max={100}
                  step="0.01"
                  value={formData.twelfthPercentage}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none'
                  } as React.CSSProperties}
                />
              </div>

              {/* College/University Percentage */}
              <div>
                <label htmlFor="collegePercentage" className="block text-sm font-medium text-gray-700 mb-1">
                  College/University Percentage *
                </label>
                <input
                  type="number"
                  id="collegePercentage"
                  name="collegePercentage"
                  required
                  min={0}
                  max={100}
                  step="0.01"
                  value={formData.collegePercentage}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none'
                  } as React.CSSProperties}
                />
              </div>

              <div>
                <CollegeSelect id="college" value={selectedCollege} onChange={college => {
                  setSelectedCollege(college)
                  setFormData(previous => ({ ...previous, college: college?.college_name || '' }))
                }} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="course" className="block text-sm font-medium text-gray-700 mb-1">
                    Course/Program *
                  </label>
                  <input
                    type="text"
                    id="course"
                    name="course"
                    required
                    value={formData.course}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="branch" className="block text-sm font-medium text-gray-700 mb-1">
                    Branch *
                  </label>
                  <input
                    type="text"
                    id="branch"
                    name="branch"
                    required
                    value={formData.branch}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="collegePassoutYear" className="block text-sm font-medium text-gray-700 mb-1">
                    College Passout Year *
                  </label>
                  <select
                    id="collegePassoutYear"
                    name="collegePassoutYear"
                    required
                    value={formData.collegePassoutYear}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Year</option>
                    {(() => {
                      const years: number[] = []
                      for (let y = 2018; y <= 2028; y++) years.push(y)
                      return years.map(y => (
                        <option key={y} value={String(y)}>{y}</option>
                      ))
                    })()}
                  </select>
                </div>
                {formData.collegePassoutYear && (
                  <div>
                    <label htmlFor="collegePassoutMonth" className="block text-sm font-medium text-gray-700 mb-1">
                      Month of Passing *
                    </label>
                    <select
                      id="collegePassoutMonth"
                      name="collegePassoutMonth"
                      required
                      value={formData.collegePassoutMonth}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Month</option>
                      <option value="January">January</option>
                      <option value="February">February</option>
                      <option value="March">March</option>
                      <option value="April">April</option>
                      <option value="May">May</option>
                      <option value="June">June</option>
                      <option value="July">July</option>
                      <option value="August">August</option>
                      <option value="September">September</option>
                      <option value="October">October</option>
                      <option value="November">November</option>
                      <option value="December">December</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="rollNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Roll Number/Student ID *
                </label>
                <input
                  type="text"
                  id="rollNumber"
                  name="rollNumber"
                  required
                  value={formData.rollNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Resume Upload */}
              <div>
                <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-1">
                  Resume/CV *
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  {formData.resume && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center text-sm text-green-700">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">{formData.resume.name}</span>
                        <span className="ml-2 text-green-600">
                          ({(formData.resume.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Upload your resume in PDF format only (max 5MB)
                </p>
              </div>

              <button
                type="submit"
                disabled={isRegistering || driveInfo.registered_candidates >= driveInfo.max_candidates}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRegistering ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Registering...
                  </>
                ) : (
                  <>
                    <span>Register & Login</span>
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>

              {driveInfo.registered_candidates >= driveInfo.max_candidates && (
                <p className="text-red-600 text-sm text-center">
                  Maximum candidates reached. Registration is closed.
                </p>
              )}
            </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DriveRegistrationPage
